import { useMemo } from "react";
import { Icon, Stack, Title } from "@introist/react-foundation/v2";
import styled from "styled-components";
import { api } from "services/rpc/RpcProvider";

type AccessIndicatorProps = {
  resourceId: string;
  resourceType: "folder" | "workflow";
};

const AccessBadge = styled(Stack)`
  padding: 0.25rem 0.5rem;
  border-radius: var(--rounding-medium);
  background: var(--palette-surface-subdued);
`;

export const AccessIndicator = ({ resourceId, resourceType }: AccessIndicatorProps) => {
  const { data: allUsers } = api.users.list.useQuery({});

  // Only fetch grants for regular users (admins/owners have full access by default)
  const regularUsers = useMemo(() => (allUsers ?? []).filter(user => user.role === "user"), [allUsers]);

  // Fetch grants for regular users and filter for this resource
  const userGrantQueries = api.useQueries(t =>
    regularUsers.map(user => t.workflows.v4.access.list({ userId: user.userId }))
  );

  const grantCount = useMemo(() => {
    // Count how many regular users have grants for this specific resource
    let count = 0;
    userGrantQueries.forEach((query, index) => {
      if (query.data) {
        const hasGrant = query.data.some(g =>
          resourceType === "folder" ? g.folderId === resourceId : g.workflowId === resourceId
        );
        if (hasGrant) count++;
      }
    });
    return count;
  }, [userGrantQueries, resourceId, resourceType]);

  if (grantCount === 0) return null;

  return (
    <AccessBadge gap="xSmall" alignItems="center">
      <Icon name="persons" />
      <Title small variant="bold">
        {grantCount}
      </Title>
    </AccessBadge>
  );
};
